import { SkyButton, SkyCard, SkyText } from "@skypointcloud/skypoint-ui";
import type { ComponentType, ReactNode } from "react";
import { Link } from "wouter";
import type { Overview, OverviewSection } from "@shared/types/overview";
import { RetryIcon } from "@/lib/icons";
import { absolutePath } from "@/lib/routes";
import { QuietMessageBar } from "./QuietMessageBar";

/**
 * A card's state as a tone (round 4): the colour of its title chip. `info` is a card with no
 * state of its own (Schedules, the last runs); the rest follow what the card counts.
 */
export type CardTone = "info" | "success" | "warning" | "destructive";

/**
 * The chip's classes, written out whole so Tailwind sees each one. Semantic subtle tokens only
 * (`scripts/check-palette.mjs`): skyAdvisor's raw palette chips would fail the gate and
 * would not change in dark mode.
 */
const CHIP_TONE: Record<CardTone, string> = {
  info: "bg-info-subtle text-info-subtle-foreground",
  success: "bg-success-subtle text-success-subtle-foreground",
  warning: "bg-warning-subtle text-warning-subtle-foreground",
  destructive: "bg-destructive-subtle text-destructive-subtle-foreground",
};

/** The tone of a status: a failure first, then a warning, then success when anything ran. */
export function statusTone(counts: { failed: number; warning: number; ok: number }): CardTone {
  if (counts.failed > 0) return "destructive";
  if (counts.warning > 0) return "warning";
  return counts.ok > 0 ? "success" : "info";
}

/**
 * The frame every Overview card shares (Session 10, B1): a `SkyCard` named by its own `h2`, so
 * a screen reader lists the cards as the page's sections, under the shell's one `h1`.
 *
 * Round 4 (owner, 2026-09-30: "Dashboard has too much white"): the title leads with the card's
 * icon in a 32 px chip tinted by `tone`. The chip is decoration (`aria-hidden`): the counts say
 * in words what its colour says, so colour is never the only signal (rules.md §7).
 */
export function OverviewCard({
  id,
  title,
  icon: Icon,
  tone = "info",
  action,
  testId,
  children,
}: {
  /** The card's slug; the heading's id is `overview-<id>-title`. */
  id: string;
  title: string;
  /** The card's icon from `@/lib/icons`; the chip is `overview-<id>-icon`. */
  icon: ComponentType<{ className?: string }>;
  tone?: CardTone;
  action?: ReactNode;
  testId: string;
  children: ReactNode;
}) {
  const headingId = `overview-${id}-title`;
  return (
    <SkyCard
      appearance="outline"
      testId={testId}
      aria-labelledby={headingId}
      // Round 6 walk W1: a grid item's `min-width: auto` is its longest unbroken line (a run id,
      // a quoted relation name), which pushed the card past its column. The card may shrink to
      // its column, and a token too long for it breaks rather than widening it; ordinary text
      // still wraps at spaces first.
      className="min-w-0 [overflow-wrap:anywhere]"
      header={{
        title: (
          <span className="flex min-w-0 items-center gap-2">
            <span
              aria-hidden="true"
              data-testid={`overview-${id}-icon`}
              data-tone={tone}
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${CHIP_TONE[tone]}`}
            >
              <Icon className="h-4 w-4" />
            </span>
            <SkyText
              as="h2"
              id={headingId}
              size={400}
              weight="semibold"
              block
              // Focusable from code only (never a tab stop): a section's Retry that succeeds
              // unmounts the focused button, and focus moves here instead of to <body>.
              tabIndex={-1}
              // #1364: the card lets a long token break anywhere, which split "Transformation" mid-word
              // at 768 px. A title wraps between its words only.
              className="min-w-0 [overflow-wrap:normal] focus:outline-none"
            >
              {title}
            </SkyText>
          </span>
        ),
        // Round 7 T-37: a card's action link is body text, 14 px (it inherited the page's 16).
        action: action ? <span className="text-sm">{action}</span> : undefined,
      }}
    >
      {children}
    </SkyCard>
  );
}

/** What a section is: readable, hidden because the role cannot read it, or failed. */
export type SectionState = "ok" | "forbidden" | "error";

/**
 * The state of one section. A `null` section with no reason is treated as an error rather than
 * as empty: the endpoint never sends that, and if it ever did, "Could not load" is honest where
 * a zero would not be.
 */
export function sectionState(overview: Overview, section: OverviewSection): SectionState {
  const reason = overview.unavailable[section];
  if (reason === "forbidden") return "forbidden";
  if (reason === "error") return "error";
  return overview[section] === null ? "error" : "ok";
}

/**
 * A section whose read failed: "Could not load" and a Retry, never a zero (contract §3). Retry
 * re-reads the whole overview, which is the only read there is.
 */
export function SectionError({
  section,
  onRetry,
  retrying,
}: {
  section: OverviewSection;
  /** Called with this section, so the screen can move focus to its card when it comes back. */
  onRetry: (section: OverviewSection) => void;
  retrying: boolean;
}) {
  return (
    <QuietMessageBar
      intent="error"
      layout="multiline"
      title="Could not load"
      message="This part of the overview could not be read. The other cards are up to date."
      testId={`overview-${section}-error`}
      actions={
        <SkyButton
          size="small"
          icon={RetryIcon}
          loading={retrying}
          onClick={() => onRetry(section)}
          testId={`overview-${section}-retry`}
        >
          Retry
        </SkyButton>
      }
    />
  );
}

/**
 * A link's colour. `accent` is every ordinary link; a status count's link takes its status's
 * colour instead (round 4): `destructive` or `warning` when it is not zero, `neutral` (the text
 * colour) at zero, so a zero failure count never reads like a warning in the link's amber.
 */
export type LinkTone = "accent" | "neutral" | "destructive" | "warning";

const LINK_TONE: Record<LinkTone, string> = {
  accent: "text-accent-text",
  neutral: "text-foreground",
  destructive: "text-destructive",
  // The warning fill (amber) reads about 2:1 on white; its subtle foreground is the text shade.
  warning: "text-warning-subtle-foreground",
};

/** A link to another screen, from inside the nested `/overview` route (`absolutePath`). */
export function OverviewLink({
  to,
  children,
  testId,
  ariaLabel,
  tone = "accent",
}: {
  to: string;
  children: ReactNode;
  testId?: string;
  ariaLabel?: string;
  tone?: LinkTone;
}) {
  return (
    <Link
      href={absolutePath(to)}
      className={`rounded-sm ${LINK_TONE[tone]} underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring`}
      data-testid={testId}
      aria-label={ariaLabel}
    >
      {children}
    </Link>
  );
}

/** One figure in a card's facts list: a term and its value, as a `dt`/`dd` pair. */
export function Fact({
  term,
  children,
  testId,
}: {
  term: string;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col" data-testid={testId}>
      <dt>
        <SkyText size={200} color="muted">
          {term}
        </SkyText>
      </dt>
      <dd>
        <SkyText size={500} weight="semibold" font="numeric" block>
          {children}
        </SkyText>
      </dd>
    </div>
  );
}
